import { Effect, Semaphore } from 'effect';
import type * as PDFJSLib from 'pdfjs-dist';

import {
  attemptStudio,
  attemptStudioPromise,
  runStudioPromise,
  runStudioSync,
  type StudioRuntimeError,
} from '../effect-runtime';

let cached: typeof PDFJSLib | null = null;

// pdfjs-dist 5.x uses the TC39 Map.prototype.getOrInsert{,Computed} proposal,
// which ships only in very recent browsers (Chrome 138+, Safari 26+). On older
// browsers the call throws the moment a PDF is parsed. Install the polyfill
// once, before any pdfjs code runs. (Ported from apps/web _shared/pdf/pdfjs.ts.)
function installMapUpsertPolyfill() {
  const proto = Map.prototype as unknown as {
    getOrInsert?: <K, V>(key: K, value: V) => V;
    getOrInsertComputed?: <K, V>(key: K, callbackfn: (key: K) => V) => V;
  };
  if (typeof proto.getOrInsertComputed !== 'function') {
    Object.defineProperty(Map.prototype, 'getOrInsertComputed', {
      configurable: true,
      writable: true,
      value: function getOrInsertComputed<K, V>(
        this: Map<K, V>,
        key: K,
        callbackfn: (key: K) => V,
      ): V {
        if (this.has(key)) return this.get(key) as V;
        const value = callbackfn(key);
        this.set(key, value);
        return value;
      },
    });
  }
  if (typeof proto.getOrInsert !== 'function') {
    Object.defineProperty(Map.prototype, 'getOrInsert', {
      configurable: true,
      writable: true,
      value: function getOrInsert<K, V>(this: Map<K, V>, key: K, value: V): V {
        if (this.has(key)) return this.get(key) as V;
        this.set(key, value);
        return value;
      },
    });
  }
}

const runtimeLoading = Semaphore.makeUnsafe(1);
/** A document may destroy its loading task, but the shared worker belongs to the runtime. */
export const makeStudioPdfjsRuntimeEffect = Effect.fn('studio.pdfjsRuntime')(function* (
  lib: typeof PDFJSLib,
  createWorker: () => Worker,
  onReady: (runtime: typeof PDFJSLib) => void = () => undefined,
) {
  let adopted = false;
  let shared: PDFJSLib.PDFWorker | undefined;
  const port = yield* Effect.acquireRelease(
    attemptStudio('pdfjs', 'runtime-port', createWorker),
    (port) =>
      adopted
        ? Effect.void
        : attemptStudio('pdfjs', 'runtime-release', () => {
            try {
              shared?.destroy();
            } finally {
              port.terminate();
            }
          }).pipe(Effect.ignore),
  );
  shared = yield* attemptStudio('pdfjs', 'runtime-worker', () => {
    const worker = lib.PDFWorker.create({ port });
    void worker.promise.catch(() => undefined);
    return worker;
  });
  yield* attemptStudioPromise('pdfjs', 'runtime-ready', () => shared!.promise);
  const worker = shared;
  const getDocument: typeof lib.getDocument = (source) => {
    const options =
      typeof source === 'string' || source instanceof URL
        ? { url: source }
        : source instanceof ArrayBuffer || ArrayBuffer.isView(source)
          ? { data: source }
          : (source ?? {});
    return lib.getDocument({
      ...options,
      worker: 'worker' in options ? (options.worker ?? worker) : worker,
    });
  };
  return yield* attemptStudio('pdfjs', 'runtime-adopt', () => {
    const runtime = { ...lib, getDocument };
    onReady(runtime);
    adopted = true;
    return runtime;
  });
}, Effect.scoped);

export const getPdfjsEffect = Effect.fn('studio.getPdfjs')(function* (): Effect.fn.Return<
  typeof PDFJSLib,
  StudioRuntimeError
> {
  if (cached) return cached;
  yield* attemptStudio('pdfjs', 'runtime-polyfill', installMapUpsertPolyfill);
  const lib = yield* attemptStudioPromise('pdfjs', 'runtime-load', () => import('pdfjs-dist'));
  return yield* makeStudioPdfjsRuntimeEffect(
    lib,
    () =>
      new Worker(new URL('./pdfjs.worker.ts', import.meta.url), {
        type: 'module',
      }),
    (runtime) => {
      cached = runtime;
    },
  );
}, runtimeLoading.withPermit);

export function getPdfjs(): Promise<typeof PDFJSLib> {
  return runStudioPromise(getPdfjsEffect());
}

export type PdfjsDocument = PDFJSLib.PDFDocumentProxy;

// --- Document lifecycle serialization ---------------------------------------
// Studio documents share the explicit runtime-owned worker above. Keep the
// established destroy-before-open ordering so document transport cleanup
// settles before another loading task begins. Document destruction no longer
// owns the shared PDFWorker wrapper or its underlying browser Worker.
// Review finding: https://github.com/steventsao/okra/issues/979

const pendingDestroys = new Set<Promise<unknown>>();

/** Register a pdf.js destroy() promise so the next getDocument waits for it. */
export function trackPdfjsDestroy(destroy: Promise<unknown>): void {
  runStudioSync(trackPdfjsDestroyEffect(destroy));
}

export function trackPdfjsDestroyEffect(
  destroy: Promise<unknown>,
): Effect.Effect<void, StudioRuntimeError> {
  return Effect.sync(() => {
    const tracked = Promise.resolve(destroy).catch(() => undefined);
    pendingDestroys.add(tracked);
    void tracked.finally(() => pendingDestroys.delete(tracked));
  });
}

/** Resolve once every destroy registered so far has settled. */
export function waitForPdfjsDestroysEffect(): Effect.Effect<void, StudioRuntimeError> {
  return attemptStudioPromise('pdfjs', 'document-destroy', async () => {
    await Promise.allSettled([...pendingDestroys]);
  });
}

export function waitForPdfjsDestroys(): Promise<void> {
  return runStudioPromise(waitForPdfjsDestroysEffect());
}
